<template>
    <div>
        <v-dialog v-model="crdMatchDialog" max-width="700px">
            <v-card>
                <v-card-title class="headline primary darken-1 white--text">Select FINRA Match</v-card-title>
                <v-card-text class="pt-5">
                    <v-autocomplete
                        v-model="selectedCrdMatch"
                        :items="crdMatches"
                        item-text="display_label"
                        return-object
                        label="Possible matches"
                        outlined>
                    </v-autocomplete>

                    <div v-if="selectedCrdMatch" class="mt-2 general_grey--text text--darken-1">
                        <div>Name: {{ toDisplayCase(selectedCrdMatch.first_name) }} {{ toDisplayCase(selectedCrdMatch.last_name) }}</div>
                        <div>CRD Number: {{ selectedCrdMatch.crd_number }}</div>
                        <div>Firm: {{ formatFirmDisplay(selectedCrdMatch.firm) || 'N/A' }}</div>
                        <div>Location: {{ formatCityState(selectedCrdMatch.city, selectedCrdMatch.state) || 'N/A' }}</div>
                    </div>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn outlined color="primary" @click="closeCrdMatchDialog">Cancel</v-btn>
                    <v-btn color="primary" :loading="crdLookupLoading" @click="saveSelectedCrdMatch">Submit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-text-field
            :key="`crd-field-${showVerifiedCrdIcon ? 'verified' : 'unverified'}`"
            v-model="updateUser.crd_number"
            :rules="crdRules"
            :append-icon="'mdi-account-search'"
            :loading="crdLookupLoading"
            @click:append="triggerCrdLookup"
            label="CRD Number">
            <template v-slot:label>
                <span>CRD Number</span>
                <v-icon v-if="showVerifiedCrdIcon" small color="primary" class="ml-1 mt-n1">mdi-check-decagram</v-icon>
            </template>
        </v-text-field>
        <div class="caption general_grey--text text--darken-1 mt-n1 mb-4">Disclosures: {{ formatDisclosures(updateUser.disclosures) }}</div>
    </div>
</template>

<script>
    import axios from 'axios'
    import Cookie from 'js-cookie'
    import appUrls from '../../helpers/environment'

    export default {
        name: 'CrdLookupField',

        props: {
            updateUser: {
                type: Object,
                required: true
            },
            userFeedback: {
                type: Function,
                required: true
            }
        },

        data () {
            return {
                crdLookupLoading: false,
                crdMatchDialog: false,
                crdMatches: [],
                selectedCrdMatch: null,
                preserveCrdOnDialogClose: false,
                hasSavedCrd: false,
                verifiedCrdNumber: '',
                loadedCrdNumber: '',
                loadedCrdVerified: false,
                lookupStartCrdNumber: '',
                lookupStartCrdVerified: false,
                lookupStartFromDatabase: false,
                crdRules: [
                    (v) => !v || /^\d+$/.test(String(v).trim()) || 'CRD Number must be numeric'
                ]
            }
        },

        computed: {
            showVerifiedCrdIcon: function () {
                const crd = String(this.updateUser && this.updateUser.crd_number || '').trim()
                return Boolean(this.hasSavedCrd && crd)
            }
        },

        watch: {
            'updateUser.user_id': {
                handler () {
                    const currentCrd = this.updateUser && String(this.updateUser.crd_number || '').trim()
                    const isVerified = Boolean(this.updateUser && this.updateUser.crd_verified)
                    this.hasSavedCrd = !!(isVerified && currentCrd)
                    this.verifiedCrdNumber = this.hasSavedCrd ? currentCrd : ''
                    this.loadedCrdNumber = currentCrd
                    this.loadedCrdVerified = isVerified
                    this.lookupStartCrdNumber = ''
                    this.lookupStartCrdVerified = false
                    this.lookupStartFromDatabase = false
                    this.crdMatchDialog = false
                    this.crdMatches = []
                    this.selectedCrdMatch = null
                },
                immediate: true
            },

            'updateUser.crd_number': function ( newValue ) {
                const currentValue = String(newValue || '').trim()
                if ( this.hasSavedCrd && currentValue !== this.verifiedCrdNumber ) {
                    this.hasSavedCrd = false
                    this.verifiedCrdNumber = ''
                    this.updateUser.crd_verified = false
                }
            },

            crdMatchDialog ( isOpen, wasOpen ) {
                if ( wasOpen && !isOpen ) {
                    if ( this.preserveCrdOnDialogClose ) {
                        this.preserveCrdOnDialogClose = false
                        this.crdMatches = []
                        this.selectedCrdMatch = null
                        return
                    }

                    if ( !this.selectedCrdMatch && !this.crdLookupLoading ) {
                        this.clearPendingCrdLookup()
                    }
                }
            }
        },

        methods: {
            formatDisclosures: function ( value ) {
                if ( value === null || value === undefined ) return ''
                return value === true ? 'Yes' : 'No'
            },

            toDisplayCase: function ( value ) {
                const text = String(value || '').trim()
                if ( !text ) return ''

                return text
                    .toLowerCase()
                    .replace(/\b([a-z])/g, (match) => match.toUpperCase())
                    .replace(/(['-][a-z])/g, (match) => match.toUpperCase())
            },

            formatFirmDisplay: function ( value ) {
                const text = String(value || '').trim()
                if ( !text ) return ''

                return this.toDisplayCase(text).replace(/\b([A-Za-z]{2,5})\b/g, (match, word, offset) => {
                    const originalWord = text.substr(offset, word.length)
                    return /^[A-Z]{2,5}$/.test(originalWord) ? originalWord : match
                })
            },

            formatCityState: function ( city, state ) {
                return [this.toDisplayCase(city), String(state || '').trim().toUpperCase()].filter(Boolean).join(', ')
            },

            formatLocationDisplay: function ( city, state ) {
                const location = this.formatCityState(city, state)
                return location ? `(${location})` : '(N/A)'
            },

            clearPendingCrdLookup: function () {
                if ( this.lookupStartFromDatabase && this.lookupStartCrdNumber ) {
                    this.updateUser.crd_number = this.lookupStartCrdNumber
                    this.updateUser.crd_verified = this.lookupStartCrdVerified
                    this.hasSavedCrd = Boolean(this.lookupStartCrdVerified)
                    this.verifiedCrdNumber = this.hasSavedCrd ? this.lookupStartCrdNumber : ''
                } else {
                    this.updateUser.crd_number = ''
                    this.updateUser.crd_verified = false
                    this.updateUser.disclosures = null
                    this.hasSavedCrd = false
                    this.verifiedCrdNumber = ''
                }

                this.lookupStartCrdNumber = ''
                this.lookupStartCrdVerified = false
                this.lookupStartFromDatabase = false
                this.crdMatches = []
                this.selectedCrdMatch = null
                this.crdMatchDialog = false
            },

            closeCrdMatchDialog: function () {
                this.crdMatchDialog = false
            },

            triggerCrdLookup: async function () {
                const crd = String(this.updateUser.crd_number || '').trim()
                if ( crd && !/^\d+$/.test(crd) ) {
                    this.userFeedback(true, 'CRD Number must be numeric.', 'accent')
                    return
                }

                const firstName = String(this.updateUser.first_name || '').trim()
                const lastName = String(this.updateUser.last_name || '').trim()
                if ( !firstName || !lastName ) {
                    this.userFeedback(true, 'First and Last Name are required for FINRA lookup.', 'accent')
                    return
                }

                this.lookupStartCrdNumber = crd
                this.lookupStartCrdVerified = Boolean(this.updateUser.crd_verified)
                this.lookupStartFromDatabase = Boolean(this.loadedCrdNumber && crd === this.loadedCrdNumber)

                this.crdLookupLoading = true
                try {
                    const rs = await axios.post(`${appUrls.api}/api/admin/crd_lookup`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        data: {
                            user_id: this.updateUser.user_id,
                            crd_number: crd,
                            first_name: firstName,
                            last_name: lastName,
                            company: String(this.updateUser.company || '').trim(),
                            city: String(this.updateUser.city || '').trim(),
                            state: String(this.updateUser.state || '').trim()
                        }
                    })

                    if ( !rs.data.outcome ) {
                        this.userFeedback(true, rs.data.message || 'There was a problem validating the CRD Number.', 'accent')
                        return
                    }

                    this.crdMatches = (rs.data.matches || []).map((item) => ({
                        ...item,
                        display_label: `${this.toDisplayCase(item.first_name)} ${this.toDisplayCase(item.last_name)} [ CRD: ${item.crd_number || 'N/A'} | ${this.formatFirmDisplay(item.firm) || 'N/A'} ${this.formatLocationDisplay(item.city, item.state)} ]`
                    }))

                    if ( !this.crdMatches.length ) {
                        this.userFeedback(true, 'The CRD Number and name did not match any FINRA records.', 'accent')
                        return
                    }

                    this.selectedCrdMatch = null
                    this.crdMatchDialog = true
                } catch ( err ) {
                    console.log(err)
                    this.userFeedback(true, 'There was a problem validating the CRD Number.', 'accent')
                } finally {
                    this.crdLookupLoading = false
                }
            },

            saveSelectedCrdMatch: async function () {
                if ( !this.selectedCrdMatch ) {
                    this.userFeedback(true, 'Please select a FINRA match before submitting.', 'accent')
                    return
                }

                this.crdLookupLoading = true
                try {
                    const rs = await axios.post(`${appUrls.api}/api/admin/crd_select_save`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        data: {
                            user_id: this.updateUser.user_id,
                            selected: this.selectedCrdMatch
                        }
                    })

                    if ( !rs.data.outcome ) {
                        this.userFeedback(true, rs.data.message || 'There was a problem saving the selected CRD match.', 'accent')
                        return
                    }

                    const savedMatch = rs.data.match || {}
                    this.updateUser.crd_number = savedMatch.crd_number || this.selectedCrdMatch.crd_number
                    this.updateUser.disclosures = savedMatch.disclosures
                    this.updateUser.crd_verified = savedMatch.crd_verified === undefined ? true : Boolean(savedMatch.crd_verified)
                    this.hasSavedCrd = Boolean(this.updateUser.crd_verified)
                    this.verifiedCrdNumber = String(this.updateUser.crd_number || '').trim()
                    this.loadedCrdNumber = this.verifiedCrdNumber
                    this.loadedCrdVerified = this.hasSavedCrd
                    this.lookupStartCrdNumber = ''
                    this.lookupStartCrdVerified = false
                    this.lookupStartFromDatabase = false
                    this.preserveCrdOnDialogClose = true
                    this.crdMatchDialog = false
                    this.crdMatches = []
                    this.selectedCrdMatch = null
                    this.userFeedback(true, rs.data.message || 'Selected CRD and disclosures were saved successfully.', 'light-green')
                } catch ( err ) {
                    console.log(err)
                    this.userFeedback(true, 'There was a problem saving the selected CRD match.', 'accent')
                } finally {
                    this.crdLookupLoading = false
                }
            }
        }
    }
</script>


// WEBPACK FOOTER //
// src/components/shared/CrdLookupField.vue